/// <amd-module name="Build/Admin/AgentCloudsList/Components/AgentCloudsList" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";

import { Button } from "VSSUI/Button";
import { Table, ITableColumn, ITableRow, TableColumnStyle, TwoLineTableCell, TableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { TaskAgentCloud } from "DistributedTask/Client/Contracts/TaskAgent";

import { PoolTypeIcon } from "Build/Admin/Common/Components/PoolTypeIcon";

import * as Resources from "Build/Admin/AgentCloudsList/Resources";

export interface IAgentCloudsListProps {
    agentClouds: TaskAgentCloud[];
    onDeleteAgentCloud(agentCloud: TaskAgentCloud): void;
    pageContext: IVssPageContext;
}

class TableListItem {
    value: TaskAgentCloud;
}

export class AgentCloudsList extends React.PureComponent<IAgentCloudsListProps> {
    private readonly _columns: ITableColumn<TableListItem>[];

    constructor(props: IAgentCloudsListProps) {
        super(props);
        this._columns = this._buildColumns();
    }

    public render(): JSX.Element {
        const itemProvider = new ArrayItemProvider<TableListItem>(this._buildListItems());
        return (
            <Table<TableListItem>
                className="pipelines-agent-clouds-list"
                columns={this._columns}
                itemProvider={itemProvider}
                onActivate={this._onRowClicked}
            />
        );
    }

    private _buildListItems(): TableListItem[] {
        let items: TableListItem[] = [];

        this.props.agentClouds.forEach(agentCloud => {
            items.push({ value: agentCloud });
        });

        return items;
    }

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, row: ITableRow<TableListItem>): void => {
        
    }

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                line1={this._renderPrimaryNameRowText(tableItem.value)}
                line2={this._renderSecondaryNameRowText(tableItem.value)}
                iconProps={{
                    render: (className?: string | undefined): JSX.Element => this._renderPoolTypeIcon(className)
                }}
            />
        );
    }

    private _renderPoolTypeIcon(className?: string | undefined): JSX.Element {
        return <PoolTypeIcon isHosted={true} className={className} />;
    }

    private _renderPrimaryNameRowText(agentCloud: TaskAgentCloud): JSX.Element {
        return (
            <Tooltip overflowOnly={true}>
                <span className="fontWeightSemiBold fontSizeM text-ellipsis">
                    {agentCloud.name}
                </span>
            </Tooltip>
        );
    }

    private _renderSecondaryNameRowText(agentCloud: TaskAgentCloud): JSX.Element {
        return (
            <span className="fontSize secondary-text flex-row flex-baseline">
                {agentCloud.type}
            </span>
        );
    }

    private _renderDeleteCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <TableCell className="bolt-table-cell-side-action" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-column">
                    <Button
                        ariaLabel={Resources.Delete}
                        className="bolt-table-cell-content-reveal"
                        subtle={true}
                        excludeTabStop={true}
                        iconProps={{
                            iconName: "Delete",
                            className: "fontSizeML"
                        }}
                        onClick={e => {
                            this.props.onDeleteAgentCloud(tableItem.value);
                            e.preventDefault();
                        }}
                    />
                </div>
            </TableCell>
        );
    }

    private _buildColumns = (): ITableColumn<TableListItem>[] => {
        return [
            {
                id: "name",
                name: Resources.Name,
                width: new ObservableValue(-100),
                columnStyle: TableColumnStyle.Primary,
                renderCell: this._renderNameCell
            } as ITableColumn<TableListItem>,
            {
                id: "delete",
                width: new ObservableValue(40),
                columnStyle: TableColumnStyle.Tertiary,
                renderCell: this._renderDeleteCell
            } as ITableColumn<TableListItem>
        ];
    }
}
